<template>
  <div>
    <div class="regulatory-deadlines__top">
      <div class="cursor_hover" @click="showAnnualView">
        <TitleBlock
          :text="year"
          color="default"
          class="title-block__cal"
          :tip-text="'Toggle through year, month and day to see pertinent deadlines. Dates shaded with circles indicate pending regulatory deadlines. Larger circles indicate more deadlines on that particular day.'"
        />
      </div>
      <!-- add the dropdown for filters here-->
      <el-select
        v-model="filterValue"
        placeholder="Select"
        @change="typeSelectorChanged"
      >
        <el-option
          v-for="item in filterOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <div class="button-container">
        <DirectionButton
          :props-handle-click="prev"
          class="report-slider__pagination--prev report-slider__pagination-top--prev swiper-button-prev"
          :direction="'prev'"
          :icon="'arrow-left--white'"
        />

        <DirectionButton
          :props-handle-click="next"
          class="report-slider__pagination--next report-slider__pagination-top--next swiper-button-next"
          :direction="'next'"
          :icon="'arrow-right--white'"
        />
      </div>
    </div>
    <div v-if="!showDailyView" class="regulatory-deadlines">
      <div
        v-for="(month, index) in months"
        :key="month"
        class="regulatory-deadlines__month"
        @click="chosenMonth(String(index + 1).padStart(2, '0'))"
      >
        <div>{{ month }}</div>
        <div v-if="filteredYearData[index].count > 0">
          <PieChart
            :items="[
              parseInt(filteredYearData[index].reportingCount, 10) || 0,
              parseInt(filteredYearData[index].applicableCount, 10) || 0,
              parseInt(filteredYearData[index].effectiveCount, 10) || 0,
              parseInt(filteredYearData[index].responseCount, 10) || 0,
            ]"
            :size="Math.floor(filteredYearData[index].count / 10)"
            :min-radius="20"
            :min-stroke="4"
          >
            <template #tooltip>
              <p
                v-if="
                  filteredYearData[index]?.effectiveCount > 0
                "
                class="tip-key1"
              >
                Effective by:
                {{ filteredYearData[index].effectiveCount }}
              </p>

              <p
                v-if="
                  filteredYearData[index]?.responseCount > 0
                "
                class="tip-key2"
              >
                Respond by:
                {{ filteredYearData[index].responseCount }}
              </p>

              <p
                v-if="
                  filteredYearData[index]?.applicableCount > 0
                "
                class="tip-key3"
              >
                Applicable by:
                {{ filteredYearData[index].applicableCount }}
              </p>

              <p
                v-if="
                  filteredYearData[index]?.reportingCount > 0
                "
                class="tip-key4"
              >
                Reporting by:
                {{ filteredYearData[index].reportingCount }}
              </p>

            </template>
          </PieChart>
        </div>
      </div>
    </div>
    <div v-else class="regulatory-deadlines">
      <el-calendar v-model="value">
        <template #dateCell="{ data }">
          <div>
            {{ data.day.split("-").slice(2).join() }}
          </div>
          <div v-if="filteredMonthDataByDay[data.day]?.count > 0">
            <PieChart
              :items="[
                filteredMonthDataByDay[data.day].reportingCount || 0,
                filteredMonthDataByDay[data.day].applicableCount || 0,
                filteredMonthDataByDay[data.day].effectiveCount || 0,
                filteredMonthDataByDay[data.day].responseCount || 0,
              ]"
              :size="parseInt(filteredMonthDataByDay[data.day].count, 10)"
            >
              <template #tooltip>
                <p
                  v-if="
                    filteredMonthDataByDay[data.day]?.effectiveCount > 0
                  "
                  class="tip-key1"
                >
                  Effective by:
                  {{ filteredMonthDataByDay[data.day].effectiveCount }}
                </p>

                <p
                  v-if="filteredMonthDataByDay[data.day]?.responseCount > 0"
                  class="tip-key2"
                >
                  Respond by:
                  {{ filteredMonthDataByDay[data.day].responseCount }}
                </p>

                <p
                  v-if="filteredMonthDataByDay[data.day]?.applicableCount > 0"
                  class="tip-key3"
                >
                  Applicable by:
                  {{ filteredMonthDataByDay[data.day].applicableCount }}
                </p>

                <p
                  v-if="filteredMonthDataByDay[data.day]?.reportingCount > 0"
                  class="tip-key4"
                >
                  Reporting by:
                  {{ filteredMonthDataByDay[data.day].reportingCount }}
                </p>
              </template>
            </PieChart>
          </div>
        </template>
      </el-calendar>
    </div>
  </div>
</template>

<script>
export default {
  name: "RegulatoryDeadlines",
  components: {
    TitleBlock: () => import("@/components/elements/TitleBlock"),
    DirectionButton: () => import("@/components/elements/DirectionButton"),
    PieChart: () => import("@/components/elements/PieChart"),
  },
  props: {
    yearData: {
      type: Array,
      default: null,
    },
    monthData: {
      type: Array,
      default: null,
    },
    currentMonth: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      year: null,
      showDailyView: false,
      value: new Date(),
      previousValue: new Date(),
      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      filterValue: "all",
      filterOptions: [
        { value: "all", label: "All" },
        { value: "responseCount", label: "Respond by" },
        { value: "effectiveCount", label: "Effective by" },
        { value: "applicableCount", label: "Applicable by" },
        { value: "reportingCount", label: "Reporting by" },
      ],
    };
  },
  computed: {
    filteredMonthDataByDay() {
      // If monthData isn't set yet, return an empty object
      if (!this.monthData) {
        return {};
      }

      // for filterValue "all" return the monthData making the key the date.
      if (this.filterValue === "all") {
        return this.monthData.reduce(
          (obj, cur) => ({ ...obj, [cur.date]: cur }),
          {}
        );
      }

      // For other values of filterValue filter the monthData and set count to the same value
      // Also, make the date the key as above.
      return this.monthData
        .filter((obj) =>
          Object.prototype.hasOwnProperty.call(obj, this.filterValue)
        )
        .reduce(
          (obj, cur) => ({
            ...obj,
            [cur.date]: {
              [this.filterValue]: cur[this.filterValue],
              count: cur[this.filterValue],
            },
          }),
          {}
        );
    },
    filteredYearData() {
      if (!this.yearData) {
        return [];
      }

      if (this.filterValue === "all") {
        return this.yearData;
      }

      return this.yearData.map((item) => {
        return {
          [this.filterValue]: item[this.filterValue] || 0,
          count: item[this.filterValue] || 0,
        };
      });
    },
  },
  watch: {
    value() {
      const date = new Date(this.value);
      this.year = date.getFullYear().toString();

      if (date.getTime() !== this.previousValue?.getTime()) {
        if (date.getFullYear() !== this.previousValue?.getFullYear()) {
          this.$emit("newCalendarYearSelected", this.value);
          this.previousValue = new Date(this.value);
          return;
        }
        if (date.getMonth() !== this.previousValue?.getMonth()) {
          this.$emit("newCalendarMonthSelected", this.value);
          this.previousValue = new Date(this.value);
          return;
        }
        this.$emit("newCalendarDateSelected", this.value);
        this.previousValue = new Date(this.value);
      }
    },
  },
  created() {
    const date = new Date();
    this.year = date.getFullYear().toString();
  },
  methods: {
    typeSelectorChanged() {
      this.$emit("typeSelectorChanged", this.filterValue);
    },
    showAnnualView() {
      this.showDailyView = false;
      // this.previousValue = null;

      this.$store.commit("Updates/setData", {
        label: "deadlines",
        data: [],
      });
      this.$emit("showDeadlineArticles", false, false);
    },
    chosenMonth(month) {
      const date = `${this.year}-${month}-01T03:00:00Z`;
      this.value = new Date(date);
      this.showDailyView = true;
      this.$emit("showDeadlineArticles", true, false);
    },
    next() {
      if (!this.showDailyView) {
        let newYear = parseInt(this.year);
        newYear++;
        this.value = newYear + "-01-01";
        this.year = newYear.toString();
      } else {
        const calcDate = new Date(this.value);
        const month = calcDate.getMonth();
        this.value = calcDate.setMonth(month + 1);
      }
    },
    prev() {
      if (!this.showDailyView) {
        let newYear = parseInt(this.year);
        newYear--;
        this.value = newYear + "-01-01";
        this.year = newYear.toString();
      } else {
        const calcDate = new Date(this.value);
        const month = calcDate.getMonth();
        this.value = calcDate.setMonth(month - 1);
      }
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/helpers/variables";
@import "../../assets/styles/helpers/mixin";

.cursor_hover {
  cursor: pointer;
}

.el-calendar__button-group {
  display: none;
}

.tip-key2 {
  color: #37B8CB;
}

.tip-key1 {
  color: #86C79C;
}

.tip-key3 {
  color: #0077c1;
}

.tip-key4 {
  color: #233485;
}

.regulatory-deadlines {
  display: flex;
  flex-wrap: wrap;
  border-top: 1px dashed #c6d5e0;
  border-left: 1px dashed #c6d5e0;

  &__top {
    display: flex;
    justify-content: space-between;

    .btn-direction {
      position: relative;
      background: $corporateBlue;
      right: auto;
      left: auto;
      margin-left: 10px;

      &::before {
        display: none;
      }
    }

    .button-container {
      display: flex;
      height: 100%;
    }
  }

  .el-calendar-table {
    border: none !important;

    thead th {
      background: none !important;
      border: none !important;
    }

    tr:nth-of-type(odd) {
      background: none !important;
    }

    tbody {
      border: 1px dotted #b1b3b3 !important;
    }
  }

  .title-block__cal {
    height: 3.6rem;
    width: 75px;
  }

  .el-calendar-day {
    display: grid;
    place-items: center;
    place-content: center;
    place-self: center;

    * {
      grid-area: 1 / 1 / 2 / 2;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }

  &__month {
    width: calc(100% / 6);
    height: 9rem;
    display: grid;
    place-items: center;
    place-content: center;
    place-self: center;
    border-right: 1px dashed #c6d5e0;
    border-bottom: 1px dashed #c6d5e0;
    position: relative;
    cursor: pointer;

    * {
      grid-area: 1 / 1 / 2 / 2;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }
}
</style>
